// Deterministic element IDs derived from a field path, so labels, help text,
// and error text can be wired together (htmlFor / aria-describedby) without
// threading generated ids through props. Both the field wrapper and the control
// derive the same ids from the same path.

import type { DynamicFormField } from './types'

/** The control's `id` / label `htmlFor` target. */
export function fieldIdFor(path: string): string {
  return `dff-${path.replace(/[^a-zA-Z0-9_-]+/g, '-')}`
}

/**
 * The id of a field's label element. Every field renders a real `<label>`; this
 * is the `aria-labelledby` target for grouped controls (radio / multi-checkbox /
 * scalar-list / button toggles), whose control is a `role=group` container rather
 * than a labelable element — so the label points at them via aria instead of
 * being pointed at by `htmlFor`. See `FieldShell`'s `groupLabel`.
 */
export function labelIdFor(path: string): string {
  return `${fieldIdFor(path)}-label`
}

export function descriptionIdFor(path: string): string {
  return `${fieldIdFor(path)}-desc`
}

export function helpIdFor(path: string): string {
  return `${fieldIdFor(path)}-help`
}

export function errorIdFor(path: string): string {
  return `${fieldIdFor(path)}-error`
}

/**
 * The `aria-describedby` value for a control: the ids of whichever of
 * description / help text / error text are actually present. Returns undefined
 * when none apply (so the attribute can be omitted entirely).
 */
export function describedByFor(
  field: DynamicFormField,
  path: string,
  hasErrors: boolean,
): string | undefined {
  const ids: string[] = []
  if (field.description) ids.push(descriptionIdFor(path))
  if (field.help_text) ids.push(helpIdFor(path))
  if (hasErrors) ids.push(errorIdFor(path))
  return ids.length > 0 ? ids.join(' ') : undefined
}
